import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Check, X, Loader2 } from "lucide-react";

// Debounced handle input with live availability check + alternative suggestions.
export const HandleField = ({ value, onChange, testidPrefix = "handle", autoFocus = false }) => {
  const [state, setState] = useState({ status: "idle", available: null, suggestions: [] });
  const timer = useRef(null);

  useEffect(() => {
    const h = (value || "").trim();
    if (timer.current) clearTimeout(timer.current);
    if (!h) { setState({ status: "idle", available: null, suggestions: [] }); return; }
    if (h.length < 2) { setState({ status: "short", available: false, suggestions: [] }); return; }
    setState((s) => ({ ...s, status: "checking" }));
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/handle/check", { params: { handle: h } });
        setState({ status: "done", available: data.available, reason: data.reason, suggestions: data.suggestions || [] });
      } catch {
        setState({ status: "idle", available: null, suggestions: [] });
      }
    }, 450);
    return () => timer.current && clearTimeout(timer.current);
  }, [value]);

  return (
    <div>
      <div className="relative">
        <input
          data-testid={`${testidPrefix}-input`}
          autoFocus={autoFocus}
          placeholder="e.g. VintageVicky"
          className="w-full hairline p-3 pr-10 text-sm bg-white outline-none focus:border-black"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2">
          {state.status === "checking" && <Loader2 size={16} className="animate-spin text-neutral-400" />}
          {state.status === "done" && state.available && <Check size={16} style={{ color: "#1E7A3B" }} data-testid={`${testidPrefix}-ok`} />}
          {state.status === "done" && !state.available && <X size={16} className="text-red-500" data-testid={`${testidPrefix}-taken`} />}
        </span>
      </div>
      {state.status === "short" && <p className="mt-1.5 text-xs text-red-500">Handle must be at least 2 characters.</p>}
      {state.status === "done" && state.available && <p className="mt-1.5 text-xs" style={{ color: "#1E7A3B" }}>“{value.trim()}” is available.</p>}
      {state.status === "done" && !state.available && state.reason === "reserved" && (
        <p className="mt-1.5 text-xs text-red-500" data-testid={`${testidPrefix}-reserved`}>That handle is reserved and can't be used.</p>
      )}
      {state.status === "done" && !state.available && state.reason !== "reserved" && (
        <div className="mt-1.5 text-xs text-neutral-600" data-testid={`${testidPrefix}-suggestions`}>
          That handle is taken.{state.suggestions.length > 0 && " Try:"}
          <span className="ml-1 inline-flex flex-wrap gap-1.5 align-middle">
            {state.suggestions.map((s) => (
              <button key={s} type="button" data-testid={`${testidPrefix}-suggestion-${s}`}
                onClick={() => onChange(s)}
                className="chip !py-0.5 !px-2 !text-[0.68rem]">{s}</button>
            ))}
          </span>
        </div>
      )}
    </div>
  );
};

export default HandleField;
